import { Component, OnDestroy, effect, input, model, output, signal, untracked, ViewChild, TemplateRef } from '@angular/core';
import { ClientInformation } from '../../../../../models/client-information';
import { Integration } from '../../../../../models/integration';
import { IntegrationJavaScript } from '../../../../../models/integration-javascript';
import { MonacoPageComponent } from 'src/app/features/lt-shared-ui/lt-monaco/components/monaco-editor/monaco-editor.component';
import { ClientIntegrationService } from '../../../../../services/client-integration.service';
import { ToastService } from '../../../../../services/toast.service';
import { Subject } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { LTSwitchComponent } from 'src/app/features/lt-shared-ui/lt-button/components/lt-switch/lt-switch.component';
import { DestinationEndpoint, getEndpointName } from 'src/app/models/destination-endpoint';
import { User } from '../../../../../models/user';
import { takeUntil } from 'rxjs/operators';
import { isValidXML } from 'src/app/utilities/data-utils';

@Component({
  selector: 'app-edit-integration-config',
  imports: [
    FormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    LTSwitchComponent,
    MonacoPageComponent,
  ],
  templateUrl: './edit-integration-config.component.html',
  styleUrl: './edit-integration-config.component.scss',
})
export class EditIntegrationConfigComponent implements OnDestroy {
  @ViewChild('monaco') public monaco: MonacoPageComponent;
  @ViewChild('addJsDialog', { static: true }) addJsDialog: TemplateRef<any>;
  componentDestroyed$: Subject<boolean> = new Subject();
  client = input.required<ClientInformation>();

  integration = input.required<Integration>();
  savingInformation = model.required<boolean>();

  deleteIntegration = output<Integration>();
  selectNewIntegration = output<Integration>();
  handleGoBack = output<void>();

  destinationEndpoints = input.required<DestinationEndpoint[]>();

  validDestinations = ['CSDataSwap', 'Surefire3', 'LGDataSwap', 'SimpleNexus', 'TotalExpert'];
  availableDestinations = this.validDestinations;

  javaScript: IntegrationJavaScript;
  xmlSelected = signal<boolean>(true);
  integrationSelected = signal<boolean>(false);
  convertCreateNew = signal<boolean>(false);

  monacoData = '';
  lang = 'xml';
  originalIntegrationXML: string;
  destNameSelected: string = '';


  constructor(
    private clientService: ClientIntegrationService,
    private toastService: ToastService,
    private dialog: MatDialog,
    private user: User,
  ) {
    effect(() => {
      const integration = this.integration();
      if (integration) {
        untracked(() => this.selectIntegration());
      }
    });
  }

  ngOnDestroy() {
    this.dialog.closeAll();
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  //#region Integration
  selectIntegration(): void {
    this.originalIntegrationXML = this.integration().xmlConfig;
    this.integrationSelected.set(true);
    this.javaScript = this.integration().javaScript[0];

    if (this.xmlSelected()) {
      this.setupMonaco('xml', this.integration().xmlConfig);
    }
    else {
      this.setupMonaco('javascript', '');
      for (const js of this.integration().javaScript) {
        if (js.destinationEndpointId == this.javaScript.destinationEndpointId) {
          this.monacoData = js.javaScript;
        }
      }
    }

  }

  updateIntegration(): void {
    this.savingInformation.set(true);
    if (this.xmlSelected()) {
      this.integration().xmlConfig = this.monacoData;
    }
    else {
      for (const js of this.integration().javaScript) {
        if (js.destinationEndpointId == this.javaScript.destinationEndpointId
          && this.monacoData !== js.javaScript) {
          js.isUpdated = true;
          js.javaScript = this.monacoData;
        }
      }
    }

    const validCheck = isValidXML(this.integration().xmlConfig);
    if (validCheck[0] == false) {
      this.toastService.toastCreate('Failed to parse ' + this.integration().name + ' integration XML. Reason: ' + validCheck[1], 'Warning', {
        autoClose: false,
      });
      this.savingInformation.set(false);
      return;
    }

    this.integration().clientName = this.client().clientDisplayName;
    this.integration().updatedDate = new Date(Date.now());

    this.clientService.updateIntegration(this.integration())
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (_value) => {
          // on success
          this.toastService.toastCreate('Successfully updated ' + this.integration().name + ' integration XML.', 'Success', {
            autoClose: false,
          });

          this.originalIntegrationXML = this.integration().xmlConfig;

          for (const js of this.integration().javaScript) {
            if (js.isUpdated) {
              //New JavaScript
              if (js.integrationJavaScriptModuleId == '00000000-0000-0000-0000-000000000000') {
                this.clientService.addJavaScript(js)
                  .pipe(takeUntil(this.componentDestroyed$))
                  .subscribe({
                    next: (newJsId) => {
                      this.toastService.toastCreate('Successfully created ' + this.integration().name + ' ' + this.getEndpointName(js.destinationEndpointId) + ' JavaScript.', 'Success', {
                        autoClose: false,
                      });

                      js.integrationJavaScriptModuleId = newJsId;
                      js.isUpdated = false;
                    },
                    error: (_error) => {
                      this.toastService.toastCreate('Failed to create ' + this.integration().name + ' ' + this.getEndpointName(js.destinationEndpointId) + ' JavaScript.', 'Danger', {
                        autoClose: false,
                      });
                    },
                  });
              }
              else {
                js.updatedDate = new Date(Date.now());
                this.clientService.updateJavaScript(js)
                  .pipe(takeUntil(this.componentDestroyed$))
                  .subscribe({
                    next: (_jsID) => {
                      this.toastService.toastCreate('Successfully updated ' + this.integration().name + ' ' + this.getEndpointName(js.destinationEndpointId) + ' JavaScript.', 'Success', {
                        autoClose: false,
                      });

                      js.isUpdated = false;
                    },
                    error: (_error) => {
                      this.toastService.toastCreate('Failed to update ' + this.integration().name + ' ' + this.getEndpointName(js.destinationEndpointId) + ' JavaScript.', 'Danger', {
                        autoClose: false,
                      });
                    },
                  });
              }
            }
          }

          this.savingInformation.set(false);
        },
        error: (_error) => {
          this.savingInformation.set(false);
          this.toastService.toastCreate('Failed to update ' + this.integration().name + ' integration XML.', 'Danger', {
            autoClose: false,
          });
        },
      });
  }

  private setupMonaco(lang: string, data: string): void {
    this.monaco?.changeLanguage(lang);
    this.monacoData = data;
    this.lang = lang;
  }

  saveData(changingToXML: boolean): void {
    // false is switching to JavaScript and saving XML
    // true is switching to xmlConfig and saving javascript
    this.xmlSelected.set(changingToXML);

    if (true == changingToXML) {

      for (const js of this.integration().javaScript) {
        if (js.destinationEndpointId == this.javaScript.destinationEndpointId) {
          if (this.javaScript.javaScript != this.monacoData) {
            js.isUpdated = true;
          }
          js.javaScript = this.monacoData;
        }
      }

      this.setupMonaco('xml', this.integration().xmlConfig);
    } else {
      this.integration().xmlConfig = this.monacoData;
      this.setupMonaco('javascript', '');

      for (const js of this.integration().javaScript) {
        if (js.destinationEndpointId == this.javaScript.destinationEndpointId) {
          this.monacoData = js.javaScript;
        }
      }
    }
  }

  //#region GenericIntegration
  createGenericIntegration(): void {
    this.savingInformation.set(true);
    this.clientService.convertIntegrationToGeneric(this.integration(), this.convertCreateNew())
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (integration) => {
          if (this.convertCreateNew()) {
            //this.integrations.push(integration);
            //this.selectNewIntegration(integration);
            this.selectNewIntegration.emit(integration);
            this.savingInformation.set(false);
          }
          else {
            this.integration().xmlConfig = integration.xmlConfig;
            this.originalIntegrationXML = this.integration().xmlConfig;
            this.savingInformation.set(false);
          }
        },
        error: (_error) => {
          this.savingInformation.set(false);
        },
      });
  }

  isGenericIntegration(): boolean {
    return this.integration()?.xmlConfig?.toLowerCase().includes('dynamictargets="true"');
  }

  //#endregion

  //#region APIIntegration

  createAPIIntegration(): void {
    this.savingInformation.set(true);
    this.clientService.convertIntegrationToAPI(this.integration())
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (integration) => {
          //this.integrations().push(integration);
          //this.selectNewIntegration(integration);
          this.selectNewIntegration.emit(integration);
          this.savingInformation.set(false);
        },
        error: (_error) => {
          this.savingInformation.set(false);
          this.toastService.toastCreate('Failed to convert integration to API.', 'Warning', {
            autoClose: false,
          });
        },
      });
  }

  isAPIIntegration(): boolean {
    return this.integration()?.xmlConfig?.toLowerCase().includes('enableelliemaeapi="true"');
  }

  canDeactivate(): boolean {
    if (this.xmlSelected()) {
      if (this.integration() != null) {
        this.integration().xmlConfig = this.monacoData;
      }
    }
    else {
      for (const js of this.integration().javaScript) {
        js.isUpdated = false;
        if (js.destinationEndpointId == this.javaScript.destinationEndpointId
          && this.monacoData !== js.javaScript) {
          js.isUpdated = true;
          js.javaScript = this.monacoData;
        }
      }
    }

    let canDeactivate = true;

    if (this.integration() != null) {
      if (this.originalIntegrationXML != this.integration().xmlConfig) {
        canDeactivate = false;
      }

      for (const js of this.integration().javaScript) {
        if (js.isUpdated) {
          canDeactivate = false;
        }
      }
    }

    return canDeactivate;
  }

  //#endregion

  //#endregion


  //#region JS

  deleteJavaScript(): void {
    this.savingInformation.set(true);
    const confirmMsg =
      'Are you sure you want to delete the selected javascript?';
    if (window.confirm(confirmMsg)) {
      const destEndpointId = this.javaScript.destinationEndpointId;

      // Switch to first available javascript
      if (this.integration().javaScript[0].destinationEndpointId == this.javaScript.destinationEndpointId) {
        if (this.integration().javaScript.length != 1) {
          this.switchJavaScript(this.integration().javaScript[1]);
        }
      }
      else {
        this.switchJavaScript(this.integration().javaScript[0]);
      }

      // Remove javascript from local integration object
      for (let i = 0; i < this.integration().javaScript.length; i++) {
        if (this.integration().javaScript[i].destinationEndpointId == destEndpointId) {
          this.integration().javaScript.splice(i, 1);
        }
      }

      // Call delete javascript
      this.clientService.deleteJavaScript(this.integration().integrationId, destEndpointId)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: () => {
            this.toastService.toastCreate('Successfully deleted ' + this.getEndpointName(destEndpointId) + ' JavaScript.', 'Success', {
              autoClose: false,
            });
            this.savingInformation.set(false);
          },
          error: (_error) => {
            this.toastService.toastCreate('Failed to delete ' + this.getEndpointName(destEndpointId) + ' JavaScript.', 'Danger', {
              autoClose: false,
            });
            this.savingInformation.set(false);
          },
        });
    }
    else {
      this.savingInformation.set(false);
    }

  }

  switchJavaScript(j: IntegrationJavaScript): void {
    for (const js of this.integration().javaScript) {
      if (js.destinationEndpointId == this.javaScript.destinationEndpointId
        && this.javaScript.javaScript != this.monacoData) {
        js.isUpdated = true;
        js.javaScript = this.monacoData;
      }
    }

    this.javaScript = j;

    for (const js of this.integration().javaScript) {
      if (js.destinationEndpointId == this.javaScript.destinationEndpointId) {
        this.monacoData = js.javaScript;
      }
    }
  }

  // Open dialog box to add new javascript and only allow choices that aren't chosen yet
  addNewJavaScript(): void {
    this.availableDestinations = [];
    for (const dest of this.validDestinations) {
      this.availableDestinations.push(dest);
    }
    for (let i = 0; i < this.availableDestinations.length; i++) {
      for (let j = 0; j < this.integration().javaScript.length; j++) {
        const endpointName = this.destinationEndpoints().find(d => d.destinationEndpointId == this.integration().javaScript[j].destinationEndpointId).endpointName;
        if (this.availableDestinations[i] == endpointName) {
          this.availableDestinations.splice(i, 1);
        }
      }
    }

    this.dialog.open(this.addJsDialog);
  }

  selectNewJavaScript(destName: string): void {
    //Save any changes to existing javascript
    for (const js of this.integration().javaScript) {
      if (js.destinationEndpointId == this.javaScript.destinationEndpointId
        && this.javaScript.javaScript != this.monacoData) {
        js.isUpdated = true;
        js.javaScript = this.monacoData;
      }
    }

    const selectedEndpoint = this.destinationEndpoints().find(d => d.endpointName == destName);

    if (selectedEndpoint != undefined) {
      this.javaScript = {
        integrationJavaScriptModuleId: '00000000-0000-0000-0000-000000000000',
        integrationId: this.integration().integrationId,
        createDate: new Date(),
        updatedDate: new Date(),
        javaScript: '',
        isUpdated: true,
        destinationEndpointId: selectedEndpoint.destinationEndpointId,
      };
    }

    this.integration().javaScript[this.integration().javaScript.length] = this.javaScript;
    this.monacoData = this.javaScript.javaScript;
  }

  //#endregion

  checkAccess(role: string[]): boolean {
    return this.user.checkAccess(role);
  }
  goBack(): void {
    this.handleGoBack.emit();
  }

  handleDeleteIntegration(): void {
    this.deleteIntegration.emit(this.integration());
  }
  //#region destinations
  getEndpointName(destId: number): string {
    return getEndpointName(destId, this.destinationEndpoints());
  }
  //#endregion

}
